// Coordinate mapping between client (mouse) space and scene world space.
//
// World space == viewport resolution pixels: the layout pass uses
// `rootSpace = { max: viewportSize }`, so LayoutMap world coordinates are
// expressed in the same units as the canvas backing resolution. The canvas is
// displayed at a uniformly scaled CSS size (see syncViewportCanvasDisplay),
// hence the single ratio resolution/displayRect per axis.

import type { Vec2 } from '../scene/layoutMath.js';
import type { ViewportResolution } from './viewportResolution.js';

function getViewportCanvas(): HTMLCanvasElement | null {
  return document.getElementById('viewport-canvas') as HTMLCanvasElement | null;
}

/**
 * Map a client point (e.g. MouseEvent.clientX/Y) to scene world space.
 * Returns null if the canvas is missing or has no display area.
 */
export function screenToWorld(
  clientX: number,
  clientY: number,
  resolution: ViewportResolution,
): Vec2 | null {
  const canvas = getViewportCanvas();
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return [
    ((clientX - rect.left) / rect.width) * resolution.w,
    ((clientY - rect.top) / rect.height) * resolution.h,
  ];
}

/**
 * Map a scene world-space point to client coordinates (CSS pixels).
 * Returns null if the canvas is missing or has no display area.
 */
export function worldToScreen(
  point: Vec2,
  resolution: ViewportResolution,
): Vec2 | null {
  const canvas = getViewportCanvas();
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  if (resolution.w <= 0 || resolution.h <= 0) return null;
  return [
    rect.left + (point[0] / resolution.w) * rect.width,
    rect.top + (point[1] / resolution.h) * rect.height,
  ];
}

/** True if the client point falls within the displayed canvas rectangle. */
export function isInsideViewport(clientX: number, clientY: number): boolean {
  const canvas = getViewportCanvas();
  if (!canvas) return false;
  const rect = canvas.getBoundingClientRect();
  return (
    clientX >= rect.left
    && clientX <= rect.right
    && clientY >= rect.top
    && clientY <= rect.bottom
  );
}
